import { useState } from "react";
import { useTranslation } from "react-i18next";

const getSteps = (t: (key: string) => string) => [
  { num: "01", title: t("regStep1Title"), desc: t("regStep1Desc") },
  { num: "02", title: t("regStep2Title"), desc: t("regStep2Desc") },
  { num: "03", title: t("regStep3Title"), desc: t("regStep3Desc") },
  { num: "04", title: t("regStep4Title"), desc: t("regStep4Desc") },
  { num: "05", title: t("regStep5Title"), desc: t("regStep5Desc") },
  { num: "06", title: t("regStep6Title"), desc: t("regStep6Desc") },
];

const countries = ["Rwanda", "Kenya", "Uganda", "Tanzania", "Ethiopia", "Burundi", "DRC"];

export default function ServiceRegistration() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const steps = getSteps(t);

  return (
    <section id="service-1" className="bg-white py-20 lg:py-24">
      <div className="w-full px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 flex items-center justify-center bg-[#0A1628] rounded-xl">
                <i className="ri-building-2-line text-2xl text-white" />
              </div>
              <span className="text-xs font-semibold tracking-widest uppercase text-[#00C2C2]">{t("regServiceNum")}</span>
            </div>
            <h2 className="text-4xl font-bold text-[#0A1628] leading-tight mb-4">
              {t("regTitle")}<br />{t("regSubtitle")}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              {t("regDesc")}
            </p>
            <div className="flex flex-wrap gap-2 mb-8">
              {countries.map((c) => (
                <span key={c} className="bg-[#f5f5f0] text-[#0A1628] text-xs font-semibold px-3 py-1.5 rounded-full">
                  <i className="ri-map-pin-2-line mr-1" />{c}
                </span>
              ))}
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-[#00C2C2] text-white text-sm font-bold px-7 py-3.5 rounded-full hover:bg-[#00AAAA] transition-all whitespace-nowrap cursor-pointer"
            >
              {t("regCta")}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {steps.map((s) => (
              <div key={s.num} className="bg-[#f8fafc] rounded-xl p-5 hover:border-[#00C2C2]/30 border border-transparent transition-all">
                <div className="text-[#00C2C2] text-2xl font-bold mb-2">{s.num}</div>
                <h4 className="text-[#0A1628] font-bold text-sm mb-1">
                  <a id={`reg-step-${s.num}`}>{s.title}</a>
                </h4>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {showForm && (
          <div className="mt-12 bg-[#f8fafc] rounded-2xl p-8 max-w-2xl">
            <h3 className="text-xl font-bold text-[#0A1628] mb-6">{t("regFormTitle")}</h3>
            <form
              data-readdy-form
              id="company-registration-form"
              action="https://readdy.ai/api/form/d7jp0bm0l883cmjgvgug"
              method="POST"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
                try {
                  await fetch("https://readdy.ai/api/form/d7jp0bm0l883cmjgvgug", { method: "POST", body: data });
                  form.reset();
                  setShowForm(false);
                } catch {
                  // silent
                }
              }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("regFormName")} *</label>
                <input name="name" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2]" placeholder={t("fullName")} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("regFormEmail")} *</label>
                <input name="email" type="email" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2]" placeholder="your@email.com" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("regFormCountry")} *</label>
                <select name="country" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2] bg-white">
                  <option value="">{t("selectCountry")}</option>
                  {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-[#0A1628]">{t("regFormSector")} *</label>
                <select name="sector" required className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2] bg-white">
                  <option value="">{t("selectSector")}</option>
                  {["Health", "Agriculture", "Education", "Manufacturing", "Mining", "Technology", "Other"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-1 sm:col-span-2">
                <label className="text-xs font-semibold text-[#0A1628]">{t("regFormActivity")}</label>
                <textarea name="business_activity" maxLength={500} className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#00C2C2] resize-none h-20" placeholder="Describe your planned business activity..." />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" className="bg-[#00C2C2] text-white text-sm font-bold px-7 py-3 rounded-full hover:bg-[#00AAAA] transition-all whitespace-nowrap cursor-pointer">
                  {t("regFormSubmit")}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
